// Federaciones — DATOS REALES (tabla federaciones + conteos de GET
// organizacion/resumen: cooperativas activas, afiliados activos, aporte
// mensual). Antes esta pantalla usaba mockData; lo que no exista en la base
// de datos no se muestra ni se inventa.
//
// Alcance: el SuperAdmin / nivel confederación ve todas; los demás niveles ven
// solo la federación a la que pertenecen. La edición de una federación vive en
// «Administración de la Red → Estructura».

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  Apartment as ApartmentIcon,
  Business as BusinessIcon,
  Groups as GroupsIcon,
  Paid as PaidIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import useRedReal from '../hooks/useRedReal';
import { useAuth } from '../context/AuthContext';
import { fecha, fmt, moneda, plural } from '../utils/formato';

const grid = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 2 });

const Dato = ({ titulo, valor }) =>
  valor ? (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {titulo}
      </Typography>
      <Typography variant="body2">{valor}</Typography>
    </Box>
  ) : null;

const Federaciones = () => {
  const { federaciones, confederaciones, cooperativas, conteos, cargando, error, recargar } = useRedReal();
  const { user, contexto } = useAuth();
  const [busca, setBusca] = useState('');
  const [confFiltro, setConfFiltro] = useState('');
  const [detalle, setDetalle] = useState(null);

  const propia = user?.nivel && user.nivel !== 'confederacion' ? contexto?.federacion?.id || '__ninguna__' : null;
  const confPorId = useMemo(() => new Map(confederaciones.map((c) => [c.id, c])), [confederaciones]);

  const visibles = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return federaciones
      .filter((f) => !propia || f.id === propia)
      .filter((f) => !confFiltro || f.confederacionId === confFiltro)
      .filter((f) => !t || [f.nombre, f.siglas, f.especializacion].filter(Boolean).join(' ').toLowerCase().includes(t))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [federaciones, propia, confFiltro, busca]);

  const suma = (campo) => visibles.reduce((t, f) => t + (conteos.federaciones.get(f.id)?.[campo] || 0), 0);

  const stats = [
    { titulo: 'Federaciones', valor: fmt(visibles.length), icono: ApartmentIcon, color: '#E65100' },
    { titulo: 'Cooperativas activas', valor: fmt(suma('cooperativas')), icono: BusinessIcon, color: '#6A1B9A' },
    { titulo: 'Afiliados activos', valor: fmt(suma('afiliados')), icono: GroupsIcon, color: '#2E7D32' },
    { titulo: 'Aporte mensual', valor: moneda(suma('aporteMensual')), icono: PaidIcon, color: '#0D47A1' },
  ];

  const cooperativasDe = (federacionId) =>
    cooperativas.filter((k) => k.federacionId === federacionId).sort((a, b) => a.nombre.localeCompare(b.nombre));

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Federaciones
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Segundo piso de la estructura cooperativa
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={recargar}>
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ ...grid(220), mb: 3 }}>
            {stats.map((s) => (
              <Card key={s.titulo}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${s.color}1A`, color: s.color, display: 'flex' }}>
                    <s.icono />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: s.color }}>
                      {s.valor}
                    </Typography>
                    <Typography variant="body2">{s.titulo}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          {!propia && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              <TextField
                size="small"
                placeholder="Buscar por nombre, siglas o especialización"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                sx={{ minWidth: 320 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>Confederación</InputLabel>
                <Select label="Confederación" value={confFiltro} onChange={(e) => setConfFiltro(e.target.value)}>
                  <MenuItem value="">Todas</MenuItem>
                  {confederaciones.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {visibles.length === 0 ? (
            <Typography color="text.secondary">
              {federaciones.length === 0
                ? 'Aún no hay federaciones. Créalas o impórtalas desde «Administración de la Red».'
                : 'No hay federaciones que coincidan con el filtro.'}
            </Typography>
          ) : (
            <Box sx={grid(340)}>
              {visibles.map((f) => {
                const n = conteos.federaciones.get(f.id) || {};
                const conf = confPorId.get(f.confederacionId);
                return (
                  <Card key={f.id} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                        <Typography variant="h6">{f.nombre}</Typography>
                        {f.especializacion && <Chip size="small" label={f.especializacion} />}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {[f.siglas, conf && `${conf.nombre}${conf.pais ? ` (${conf.pais})` : ''}`, f.activa === false && 'inactiva']
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                        {[
                          [`Cooperativas${f.maxCooperativas ? ` / ${f.maxCooperativas}` : ''}`, n.cooperativas, 'secondary'],
                          ['Afiliados', n.afiliados, 'success.main'],
                        ].map(([titulo, valor, color]) => (
                          <Box key={titulo}>
                            <Typography variant="h5" fontWeight="bold" color={color}>
                              {fmt(valor)}
                            </Typography>
                            <Typography variant="caption">{titulo}</Typography>
                          </Box>
                        ))}
                      </Box>
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Aporte mensual: <b>{moneda(n.aporteMensual)}</b>
                        </Typography>
                        <Button size="small" startIcon={<VisibilityIcon />} onClick={() => setDetalle(f)}>
                          Ver detalle
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </>
      )}

      <Dialog open={Boolean(detalle)} onClose={() => setDetalle(null)} maxWidth="md" fullWidth>
        {detalle && (
          <>
            <DialogTitle>
              {detalle.nombre}
              <Typography variant="body2" color="text.secondary">
                {[detalle.siglas, detalle.especializacion].filter(Boolean).join(' · ')}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ ...grid(200), mb: 3 }}>
                <Dato titulo="NIT" valor={detalle.nit} />
                <Dato titulo="Representante legal" valor={detalle.representanteLegal} />
                <Dato titulo="Cédula del representante" valor={detalle.cedulaRepresentante} />
                <Dato titulo="Correo" valor={detalle.email} />
                <Dato titulo="Teléfono" valor={detalle.telefono} />
                <Dato titulo="Sitio web" valor={detalle.sitioWeb} />
                <Dato titulo="Dirección" valor={detalle.direccion} />
                <Dato titulo="Constituida" valor={fecha(detalle.fechaConstitucion)} />
              </Box>
              {detalle.descripcion && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {detalle.descripcion}
                </Typography>
              )}
              <Typography variant="h6" gutterBottom>
                Cooperativas
              </Typography>
              {cooperativasDe(detalle.id).length === 0 ? (
                <Typography color="text.secondary">Esta federación aún no tiene cooperativas.</Typography>
              ) : (
                <List disablePadding>
                  {cooperativasDe(detalle.id).map((k) => {
                    const n = conteos.cooperativas.get(k.id) || {};
                    return (
                      <ListItem key={k.id} divider>
                        <ListItemText
                          primary={k.nombre}
                          secondary={`${plural(n.afiliados, 'afiliado', 'afiliados')} • aporte mensual ${moneda(n.aporteMensual)}`}
                        />
                        {k.activa === false && <Chip size="small" label="Inactiva" />}
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetalle(null)}>Cerrar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Federaciones;
